// AGC: SistemaSettings - Sub-page of Settings under Segurança
// AGC: Holds system-level options (confirm before delete, categories, etc.)

import React from 'react'
import { Shield, Tag, ChevronRight, ClipboardList, Truck, Sprout, Mic } from 'lucide-react'
import { TopBar, Card } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useDeleteConfirm } from '../../context/DeleteConfirmContext'
import type { Screen } from '../../types'

interface SistemaSettingsProps {
  onBack: () => void
  onNavigate: (screen: Screen) => void
}

/**
 * AGC: System configuration page (Settings > Segurança > Sistema).
 * AGC: Currently: Confirm before delete ON/OFF. More options can be added here.
 */
export const SistemaSettings: React.FC<SistemaSettingsProps> = ({ onBack, onNavigate }) => {
  const { t } = useTranslation()
  const { confirmBeforeDelete, setConfirmBeforeDelete } = useDeleteConfirm()

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900">
      <TopBar
        title={t('profile.settingsSystem')}
        onBack={onBack}
        className="bg-white dark:bg-slate-800 shadow-sm"
      />

      <div className="px-4 py-6 pb-24">
        <Card padding="none">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {/* AGC: Confirm before delete - only option for now */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300">
                <Shield className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.confirmBeforeDelete')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.confirmBeforeDeleteDescription')}
                </p>
              </div>
              <button
                onClick={() => setConfirmBeforeDelete(!confirmBeforeDelete)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  confirmBeforeDelete ? 'bg-[#0B5F4A] dark:bg-emerald-600' : 'bg-gray-300 dark:bg-slate-600'
                } cursor-pointer`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    confirmBeforeDelete ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* AGC: Categorias - navigates to categories management sub-page */}
            <button
              type="button"
              onClick={() => onNavigate('categorias-settings')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <Tag className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.settingsCategories')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.settingsCategoriesDesc')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>

            {/* AGC: Tipos de Tarefas - task category management */}
            <button
              type="button"
              onClick={() => onNavigate('tarefas-settings')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.settingsTaskTypes')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.settingsTaskTypesDesc')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>

            {/* AGC: Tipos de Maquinário - machinery category management */}
            <button
              type="button"
              onClick={() => onNavigate('maquinario-settings')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <Truck className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.settingsMachineryTypes')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.settingsMachineryTypesDesc')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>

            {/* AGC: Tipos de Culturas - crop type management */}
            <button
              type="button"
              onClick={() => onNavigate('culturas-settings')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-lime-50 dark:bg-lime-900/30 flex items-center justify-center text-lime-600 dark:text-lime-400">
                <Sprout className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.settingsCropTypes')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.settingsCropTypesDesc')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>

            {/* AGC: Calibração de Voz - voice calibration wizard */}
            <button
              type="button"
              onClick={() => onNavigate('voice-calibration' as Screen)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Mic className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.settingsVoiceCalibration')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.settingsVoiceCalibrationDesc')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}
